import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Minus, DownloadSimple, Lightbulb } from "@phosphor-icons/react";
import { documentUrl, getConfig } from "@/lib/api";

function Section({ title, hint, children, action, progress }) {
  // progress: { done, total } — optional inline counter
  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;
  const complete = progress && progress.done === progress.total && progress.total > 0;
  return (
    <div className="bg-white border border-slate-200">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold tracking-widest text-slate-900 uppercase">{title}</h3>
            {progress && (
              <span
                data-testid="section-progress"
                className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${
                  complete
                    ? "bg-emerald-100 text-emerald-700"
                    : progress.done === 0
                    ? "bg-slate-100 text-slate-500"
                    : "bg-orange-50 text-orange-700"
                }`}
              >
                {progress.done}/{progress.total}{pct !== null && ` · ${pct}%`}
              </span>
            )}
          </div>
          {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, children, span = 1 }) {
  return (
    <div className={`md:col-span-${span}`}>
      <Label className="coord-label">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

const I = (props) => <Input {...props} className={`rounded-sm ${props.className || ""}`} />;

export default function ActaTab({ exp, setExp }) {
  const sr = exp.survey_results || {};
  const tit = exp.titulo || {};
  const ter = exp.terreno || {};
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    getConfig().then((c) => setProfile(c.agrimensor)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (field, val) =>
    setExp({ ...exp, survey_results: { ...exp.survey_results, [field]: val } });
  const updTit = (field, val) =>
    setExp({ ...exp, titulo: { ...exp.titulo, [field]: val } });
  const updTer = (field, val) =>
    setExp({ ...exp, terreno: { ...exp.terreno, [field]: val } });

  // Generic list helpers for arrays inside survey_results
  const mkList = (key, defaultRow) => {
    const list = sr[key] || [];
    return {
      list,
      add: () => upd(key, [...list, { ...defaultRow }]),
      remove: (i) => upd(key, list.filter((_, idx) => idx !== i)),
      set: (i, patch) => upd(key, list.map((r, idx) => (idx === i ? { ...r, ...patch } : r))),
    };
  };

  const hitos = mkList("hitos_existentes", { estacion: "", parcela: "", materializacion: "", observaciones: "" });
  const hitosV = mkList("hitos_vinculacion", { estacion: "", parcela: "", materializacion: "", observaciones: "" });
  const vinc = mkList("vinculaciones", { parcela: "", dc: "", municipio: "" });
  const pres = mkList("presentes_acto", { nombre: "", cedula: "", calidad: "", observaciones: "" });
  const personas = mkList("personas_campo", { nombre: "", cargo: "" });

  // Load defaults from profile into expediente (only when fields are empty)
  const applyDefaults = () => {
    if (!profile) return;
    const next = { ...sr };
    const setIfEmpty = (k, v) => {
      if (!next[k] && v) next[k] = v;
    };
    setIfEmpty("metodo_levantamiento", profile.metodo_levantamiento_default);
    setIfEmpty("cantidad_sets", profile.cantidad_sets_default);
    setIfEmpty("direccion_medicion", profile.direccion_medicion_default);
    setIfEmpty("metodo_orientacion", profile.metodo_orientacion_default);
    setIfEmpty("instrumento_marca", profile.instrumento_marca);
    setIfEmpty("instrumento_modelo", profile.instrumento_modelo);
    if ((!next.personas_campo || next.personas_campo.length === 0) && profile.personas_campo_default?.length) {
      next.personas_campo = profile.personas_campo_default.map((p) =>
        typeof p === "string" ? { nombre: p, cargo: "" } : p
      );
    }
    setExp({ ...exp, survey_results: next });
  };

  // Cálculo automático de la diferencia: presentada - derecho
  // Positivo (exceso) → "+X.XX"; Negativo (defecto) → "-X.XX"
  const parseN = (s) => {
    if (s == null || s === "") return null;
    const n = parseFloat(String(s).replace(/,/g, "").replace(/[^\d.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const areaP = parseN(sr.area_presentada);
  const areaD = parseN(sr.area_derecho);
  let diffComputed = null;
  let diffSignLabel = "";
  if (areaP !== null && areaD !== null) {
    const d = areaP - areaD;
    const sign = d >= 0 ? "+" : "-";
    diffComputed = `${sign}${Math.abs(d).toFixed(2)}`;
    diffSignLabel = d >= 0 ? "POR EXCESO" : "POR DEFECTO";
    if (Math.abs(d) < 0.005) diffSignLabel = "SIN DIFERENCIA";
  }
  // Sincroniza el valor calculado al estado si cambió
  if (diffComputed !== null && sr.diferencia_area !== diffComputed) {
    setTimeout(() => upd("diferencia_area", diffComputed), 0);
  }

  // Helpers de progreso por sección
  const filled = (v) => v != null && String(v).trim() !== "";
  const countFilled = (...vals) => vals.filter(filled).length;
  // Encabezado: operación, fecha, agrimensor (perfil), codia (perfil)
  const pEnc = { done: countFilled(exp.tipo_operacion, sr.fecha_medicion, profile?.nombre, profile?.codia), total: 4 };
  // Inmueble origen (campos clave del título)
  const pOrig = {
    done: countFilled(tit.provincia, tit.municipio, tit.parcela || tit.solar, tit.distrito_catastral, sr.calle_carretera || ter.direccion),
    total: 5,
  };
  // Descripción física: 4 materializaciones
  const pDesc = {
    done: countFilled(ter.materializacion_norte, ter.materializacion_sur, ter.materializacion_este, ter.materializacion_oeste),
    total: 4,
  };
  // Resultantes (gestionados en otra tab, pero contamos cuántas hay)
  const resCount = (sr.resultantes || []).filter((r) => filled(r?.parcela) || filled(r?.area)).length;
  const pRes = { done: resCount, total: Math.max(resCount, 1) };
  // Inicio/Fin
  const pTime = { done: countFilled(sr.fecha_medicion, sr.hora_inicio, sr.fecha_finalizacion, sr.hora_fin), total: 4 };
  // Método/Instrumento
  const pMet = {
    done: countFilled(
      sr.metodo_levantamiento || profile?.metodo_levantamiento_default,
      sr.direccion_medicion || profile?.direccion_medicion_default,
      sr.instrumento_marca || profile?.instrumento_marca,
      sr.instrumento_modelo || profile?.instrumento_modelo,
    ),
    total: 4,
  };
  // Áreas
  const pAreas = { done: countFilled(sr.area_presentada, sr.area_derecho, sr.diferencia_area), total: 3 };
  // Explique diferencia
  const pExp = { done: countFilled(sr.explique_diferencia), total: 1 };
  // Orientación
  const pOri = { done: countFilled(sr.metodo_orientacion || profile?.metodo_orientacion_default), total: 1 };
  // Vinculación (al menos 1 parcela + 1 hito ideal)
  const vincCount = (sr.vinculaciones || []).filter((v) => filled(v?.parcela)).length;
  const hvCount = (sr.hitos_vinculacion || []).filter((h) => filled(h?.estacion)).length;
  // Hitos existentes
  const hitosCount = (sr.hitos_existentes || []).filter((h) => filled(h?.estacion)).length;
  // Personas + Presentes
  const persCount = (sr.personas_campo || profile?.personas_campo_default || []).filter((p) => filled(p?.nombre || p)).length;
  const presCount = (sr.presentes_acto || []).filter((p) => filled(p?.nombre)).length;
  // Info adicional + cierre
  const pInfo = { done: countFilled(sr.informacion_adicional), total: 1 };
  const pCierre = { done: countFilled(sr.hojas_total, sr.lugar_firma_acta, sr.fecha_firma_acta), total: 3 };

  // Progreso global del Acta (suma de campos clave; las listas cuentan como 1 si tienen al menos 1 item)
  const totalDone = pEnc.done + pOrig.done + pDesc.done + pTime.done + pMet.done + pAreas.done + pExp.done + pOri.done + pInfo.done + pCierre.done
    + (resCount > 0 ? 1 : 0) + (hitosCount > 0 ? 1 : 0) + (vincCount > 0 ? 1 : 0) + (hvCount > 0 ? 1 : 0) + (persCount > 0 ? 1 : 0) + (presCount > 0 ? 1 : 0);
  const totalAll = pEnc.total + pOrig.total + pDesc.total + pTime.total + pMet.total + pAreas.total + pExp.total + pOri.total + pInfo.total + pCierre.total + 6; // 6 listas
  const globalPct = Math.round((totalDone / totalAll) * 100);

  return (
    <div className="space-y-6" data-testid="acta-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex-1 min-w-[260px]">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Acta de Hitos y Mensura — DG-RV-002</h2>
          <p className="text-slate-600 mt-1 text-sm">
            Replica fiel del formato oficial. Edita lo necesario y deja por defecto el equipo, método e instrumento del Perfil.
          </p>
          {/* Barra global de progreso */}
          <div className="mt-3 flex items-center gap-3 max-w-md" data-testid="acta-global-progress">
            <div className="flex-1 h-1.5 bg-slate-200 rounded-sm overflow-hidden">
              <div
                className={`h-full transition-all ${globalPct === 100 ? "bg-emerald-500" : "bg-orange-500"}`}
                style={{ width: `${globalPct}%` }}
              />
            </div>
            <span className="font-mono text-xs text-slate-700 whitespace-nowrap">
              {totalDone}/{totalAll} · <b>{globalPct}%</b>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="apply-defaults"
            variant="outline"
            size="sm"
            onClick={applyDefaults}
            className="rounded-sm"
            title="Llena los campos vacíos con los valores por defecto del Perfil"
          >
            <Lightbulb weight="duotone" size={14} className="mr-1.5" />
            Aplicar valores del Perfil
          </Button>
          <Button
            data-testid="gen-acta-from-tab"
            onClick={() => window.open(documentUrl(exp.id, "acta_hitos"), "_blank")}
            className="rounded-sm bg-orange-500 hover:bg-orange-400 text-white"
          >
            <DownloadSimple weight="bold" size={14} className="mr-1.5" />
            Generar PDF Acta
          </Button>
        </div>
      </div>

      {/* Encabezado: Operación, Fecha, Agrimensor, Codia */}
      <Section title="Encabezado" hint="Operación, fecha y agrimensor. La fecha de inicio se usa también para el encabezado del Acta." progress={pEnc}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Operación">
            <I data-testid="acta-operacion" value={exp.tipo_operacion || ""} onChange={(e) => setExp({ ...exp, tipo_operacion: e.target.value })} />
          </Field>
          <Field label="Fecha (inicio)">
            <I data-testid="acta-fecha" value={sr.fecha_medicion || ""} placeholder="DD/MM/AAAA" onChange={(e) => upd("fecha_medicion", e.target.value)} />
          </Field>
          <Field label="Agrimensor">
            <I value={profile?.nombre || ""} readOnly className="bg-slate-50" />
          </Field>
          <Field label="Codia">
            <I value={profile?.codia || ""} readOnly className="bg-slate-50 font-mono" />
          </Field>
        </div>
      </Section>

      {/* INMUEBLE(S) ORIGEN */}
      <Section title="Inmueble(s) Origen" hint="Datos catastrales y de ubicación del inmueble origen." progress={pOrig}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Provincia"><I value={tit.provincia || ""} onChange={(e) => updTit("provincia", e.target.value)} /></Field>
          <Field label="Municipio"><I value={tit.municipio || ""} onChange={(e) => updTit("municipio", e.target.value)} /></Field>
          <Field label="Sección"><I value={tit.seccion || ""} onChange={(e) => updTit("seccion", e.target.value)} /></Field>
          <Field label="Lugar / Sitio"><I value={tit.lugar_sitio || ""} onChange={(e) => updTit("lugar_sitio", e.target.value)} /></Field>

          <Field label="Parcela / Solar"><I className="font-mono" value={tit.parcela || ""} onChange={(e) => updTit("parcela", e.target.value)} /></Field>
          <Field label="Manzana"><I className="font-mono" value={tit.manzana || ""} onChange={(e) => updTit("manzana", e.target.value)} /></Field>
          <Field label="Porción"><I className="font-mono" value={sr.porcion || ""} onChange={(e) => upd("porcion", e.target.value)} /></Field>
          <Field label="D.C."><I className="font-mono" value={tit.distrito_catastral || ""} onChange={(e) => updTit("distrito_catastral", e.target.value)} /></Field>

          <Field label="Calle / Carretera / Camino" span={3}>
            <I value={sr.calle_carretera || ter.direccion || ""} onChange={(e) => upd("calle_carretera", e.target.value)} />
          </Field>
          <Field label="No. KM"><I className="font-mono" value={sr.no_km || ""} onChange={(e) => upd("no_km", e.target.value)} /></Field>

          <Field label="Referencias de Ubicación" span={4}>
            <Textarea
              className="rounded-sm min-h-[60px]"
              value={ter.referencias || ""}
              onChange={(e) => updTer("referencias", e.target.value)}
              placeholder="Ej. UBICADO EN LA AUTOPISTA 6 DE NOVIEMBRE..."
            />
          </Field>
        </div>
      </Section>

      {/* Descripción física + Límites */}
      <Section title="Descripción física del inmueble y su entorno" hint="Materialización por cada lindero." progress={pDesc}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Norte (materialización)"><I value={ter.materializacion_norte || ""} onChange={(e) => updTer("materializacion_norte", e.target.value)} placeholder="Ej. PARED-MALLA CICLONICA" /></Field>
          <Field label="Sur (materialización)"><I value={ter.materializacion_sur || ""} onChange={(e) => updTer("materializacion_sur", e.target.value)} /></Field>
          <Field label="Este (materialización)"><I value={ter.materializacion_este || ""} onChange={(e) => updTer("materializacion_este", e.target.value)} /></Field>
          <Field label="Oeste (materialización)"><I value={ter.materializacion_oeste || ""} onChange={(e) => updTer("materializacion_oeste", e.target.value)} /></Field>
          <Field label="Descripción del entorno / Tipo de construcción" span={2}>
            <Textarea
              className="rounded-sm min-h-[60px]"
              value={sr.descripcion_entorno || ter.tipo_construccion || ""}
              onChange={(e) => upd("descripcion_entorno", e.target.value)}
              placeholder="Ej. CASA DE DOS NIVELES, CONSTRUIDA EN BLOCKS, TECHO DE CONCRETO, PISO DE CERAMICA"
            />
          </Field>
        </div>
      </Section>

      {/* Inmuebles resultantes */}
      <Section title="Inmueble(s) Resultante(s)" hint="Las parcelas resultantes se gestionan en la pestaña Resultados. Aquí completas Manzana(s) y Porción(es)." progress={pRes}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Manzana(s) resultante(s)"><I className="font-mono" value={sr.manzanas_resultantes || ""} onChange={(e) => upd("manzanas_resultantes", e.target.value)} /></Field>
          <Field label="Porción(es) resultante(s)"><I className="font-mono" value={sr.porciones_resultantes || ""} onChange={(e) => upd("porciones_resultantes", e.target.value)} /></Field>
        </div>
      </Section>

      {/* Inicio / Fin trabajos campo */}
      <Section title="Inicio y Finalización de los Trabajos de Campo" progress={pTime}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Fecha inicio">
            <I data-testid="acta-fecha-inicio" value={sr.fecha_medicion || ""} placeholder="DD/MM/AAAA" onChange={(e) => upd("fecha_medicion", e.target.value)} />
          </Field>
          <Field label="Hora inicio">
            <I value={sr.hora_inicio || ""} placeholder="HH:MM" onChange={(e) => upd("hora_inicio", e.target.value)} />
          </Field>
          <Field label="Fecha finalización">
            <I value={sr.fecha_finalizacion || ""} placeholder="DD/MM/AAAA" onChange={(e) => upd("fecha_finalizacion", e.target.value)} />
          </Field>
          <Field label="Hora finalización">
            <I value={sr.hora_fin || ""} placeholder="HH:MM" onChange={(e) => upd("hora_fin", e.target.value)} />
          </Field>
        </div>
        {/* Fecha de los avisos */}
        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-sm">
          <Label className="coord-label text-orange-700">// FECHA DE LOS AVISOS (legal ≥ 10 días antes de medir)</Label>
          <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center gap-2">
            <I
              data-testid="acta-fecha-aviso"
              value={sr.fecha_aviso || ""}
              placeholder="DD/MM/AAAA (opcional)"
              onChange={(e) => upd("fecha_aviso", e.target.value)}
              className="max-w-[200px]"
            />
            <span className="text-xs text-slate-600">
              {sr.fecha_aviso ? (
                <>Forzada manualmente a <b className="font-mono">{sr.fecha_aviso}</b>.</>
              ) : sr.fecha_medicion ? (
                <>Se calculará automáticamente <b>11 días antes</b> de la fecha de medición.</>
              ) : (
                <>Ingresa primero la fecha de medición arriba para calcular automáticamente.</>
              )}
            </span>
          </div>
        </div>
      </Section>

      {/* Hitos existentes */}
      <Section
        title="Hitos existentes encontrados"
        hint="Estación, parcela/solar, materialización y observaciones de cada hito."
        progress={{ done: hitosCount, total: Math.max(hitosCount, 1) }}
        action={<Button size="sm" variant="outline" onClick={hitos.add} data-testid="add-hito" className="rounded-sm"><Plus size={12} className="mr-1" />Agregar</Button>}
      >
        {hitos.list.length === 0 && <div className="text-sm text-slate-500 py-2">Sin hitos. Agrega los que identificaste en campo.</div>}
        <div className="space-y-2">
          {hitos.list.map((h, i) => (
            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`hito-${i}`}>
              <I data-testid={`hito-${i}-estacion`} value={h.estacion || ""} onChange={(e) => hitos.set(i, { estacion: e.target.value })} placeholder="Estación" className="col-span-2 font-mono" />
              <I data-testid={`hito-${i}-parcela`} value={h.parcela || ""} onChange={(e) => hitos.set(i, { parcela: e.target.value })} placeholder="Parcela / Solar" className="col-span-3 font-mono" />
              <I data-testid={`hito-${i}-mat`} value={h.materializacion || ""} onChange={(e) => hitos.set(i, { materializacion: e.target.value })} placeholder="Materialización" className="col-span-4" />
              <I value={h.observaciones || ""} onChange={(e) => hitos.set(i, { observaciones: e.target.value })} placeholder="Observaciones" className="col-span-2" />
              <Button size="sm" variant="ghost" onClick={() => hitos.remove(i)} className="col-span-1 rounded-sm text-red-500"><Minus size={14} /></Button>
            </div>
          ))}
        </div>
      </Section>

      {/* Método de levantamiento + Instrumento */}
      <Section title="Método empleado para el Levantamiento" hint="Valores por defecto desde tu Perfil. Modifica solo si cambian en este expediente." progress={pMet}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Método (Sencillo / Repetitivo)">
            <Select value={sr.metodo_levantamiento || ""} onValueChange={(v) => upd("metodo_levantamiento", v)}>
              <SelectTrigger className="rounded-sm"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Sencillo">Sencillo (un set)</SelectItem>
                <SelectItem value="Repetitivo">Repetitivo (más de un set)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Cantidad de sets (si repetitivo)">
            <I className="font-mono" value={sr.cantidad_sets || ""} onChange={(e) => upd("cantidad_sets", e.target.value)} placeholder="Ej. 2" />
          </Field>
          <Field label="Dirección (Directo / Inverso)">
            <Select value={sr.direccion_medicion || ""} onValueChange={(v) => upd("direccion_medicion", v)}>
              <SelectTrigger className="rounded-sm"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Directo">Directo (radiación)</SelectItem>
                <SelectItem value="Inverso">Inverso (deflexión)</SelectItem>
                <SelectItem value="Otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Instrumento — Marca" span={1}>
            <I value={sr.instrumento_marca || profile?.instrumento_marca || ""} onChange={(e) => upd("instrumento_marca", e.target.value)} placeholder="Ej. RECEPTOR TOPCON Y ESTACION TOTAL TOPCON" />
          </Field>
          <Field label="Instrumento — Modelo" span={2}>
            <I value={sr.instrumento_modelo || profile?.instrumento_modelo || ""} onChange={(e) => upd("instrumento_modelo", e.target.value)} placeholder="Ej. HIPER LITE PLUS" />
          </Field>
        </div>
      </Section>

      {/* Áreas y diferencia */}
      <Section title="Áreas y Diferencia" progress={pAreas}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Área presentada (m²)"><I className="font-mono" value={sr.area_presentada || ""} onChange={(e) => upd("area_presentada", e.target.value)} placeholder="0.00" /></Field>
          <Field label="Área de derecho (m²)"><I className="font-mono" value={sr.area_derecho || ""} onChange={(e) => upd("area_derecho", e.target.value)} placeholder="0.00" /></Field>
          <Field label={`Diferencia (m²) — ${diffSignLabel || "automático"}`}>
            <I
              className={`font-mono ${diffComputed && diffComputed.startsWith("+") ? "bg-emerald-50 text-emerald-700" : diffComputed && diffComputed.startsWith("-") ? "bg-orange-50 text-orange-700" : "bg-slate-50"}`}
              value={sr.diferencia_area || ""}
              readOnly
              placeholder="Se calcula automáticamente"
              data-testid="diferencia-area-auto"
            />
          </Field>
        </div>
      </Section>

      {/* Explique diferencia — recuadro propio */}
      <Section title="Explique la diferencia" progress={pExp}>
        <Textarea
          data-testid="sr-explique-diff"
          className="rounded-sm min-h-[80px]"
          value={sr.explique_diferencia || ""}
          onChange={(e) => upd("explique_diferencia", e.target.value)}
          placeholder="VER INFORME TÉCNICO. PROPIETARIO CONFORME."
        />
      </Section>

      {/* Método de orientación */}
      <Section title="Método de orientación empleado" progress={pOri}>
        <I value={sr.metodo_orientacion || ""} onChange={(e) => upd("metodo_orientacion", e.target.value)} placeholder="Ej. NTRIP / Astronómico / Magnético / Otro" />
      </Section>

      {/* Vinculación parcelas */}
      <Section
        title="Vinculación — Parcelas de referencia"
        hint="Las parcelas/solares usadas para vincular la mensura."
        progress={{ done: vincCount, total: Math.max(vincCount, 1) }}
        action={<Button size="sm" variant="outline" onClick={vinc.add} data-testid="add-vinc" className="rounded-sm"><Plus size={12} className="mr-1" />Agregar</Button>}
      >
        {vinc.list.length === 0 && <div className="text-sm text-slate-500 py-2">Sin parcelas de vinculación.</div>}
        <div className="space-y-2">
          {vinc.list.map((v, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <div className="col-span-1 pt-2 font-mono text-xs text-slate-500">#{i + 1}</div>
              <I value={v.parcela || ""} onChange={(e) => vinc.set(i, { parcela: e.target.value })} placeholder="Parcela / Solar" className="col-span-5 font-mono" />
              <I value={v.dc || ""} onChange={(e) => vinc.set(i, { dc: e.target.value })} placeholder="D.C." className="col-span-2 font-mono" />
              <I value={v.municipio || ""} onChange={(e) => vinc.set(i, { municipio: e.target.value })} placeholder="Municipio" className="col-span-3" />
              <Button size="sm" variant="ghost" onClick={() => vinc.remove(i)} className="col-span-1 rounded-sm text-red-500"><Minus size={14} /></Button>
            </div>
          ))}
        </div>
      </Section>

      {/* Hitos de vinculación */}
      <Section
        title="Hitos utilizados para la vinculación (PG-XX)"
        progress={{ done: hvCount, total: Math.max(hvCount, 1) }}
        action={<Button size="sm" variant="outline" onClick={hitosV.add} data-testid="add-hito-v" className="rounded-sm"><Plus size={12} className="mr-1" />Agregar</Button>}
      >
        {hitosV.list.length === 0 && <div className="text-sm text-slate-500 py-2">Típicamente: PG-01, PG-02 (CLAVO TOPOGRÁFICO).</div>}
        <div className="space-y-2">
          {hitosV.list.map((h, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <I value={h.estacion || ""} onChange={(e) => hitosV.set(i, { estacion: e.target.value })} placeholder="PG-01" className="col-span-2 font-mono" />
              <I value={h.parcela || ""} onChange={(e) => hitosV.set(i, { parcela: e.target.value })} placeholder="Parcela / Solar" className="col-span-3 font-mono" />
              <I value={h.materializacion || ""} onChange={(e) => hitosV.set(i, { materializacion: e.target.value })} placeholder="Materialización" className="col-span-3" />
              <I value={h.observaciones || ""} onChange={(e) => hitosV.set(i, { observaciones: e.target.value })} placeholder="Observaciones (Ej. Punto Georreferenciado)" className="col-span-3" />
              <Button size="sm" variant="ghost" onClick={() => hitosV.remove(i)} className="col-span-1 rounded-sm text-red-500"><Minus size={14} /></Button>
            </div>
          ))}
        </div>
      </Section>

      {/* Información adicional */}
      <Section title="Información Adicional" progress={pInfo}>
        <Textarea
          data-testid="sr-info-adicional"
          value={sr.informacion_adicional || ""}
          onChange={(e) => upd("informacion_adicional", e.target.value)}
          className="rounded-sm min-h-[100px]"
          placeholder="Ej. LA GEORREFERENCIACIÓN DE LOS PUNTOS SE REALIZÓ UTILIZANDO EL MÉTODO NTRIP..."
        />
      </Section>

      {/* Personas auxiliares (con cargo) */}
      <Section
        title="Personas auxiliares en campo"
        hint="Nombre y cargo de quienes te asistieron. Por defecto se cargan los del Perfil."
        progress={{ done: persCount, total: Math.max(persCount, 1) }}
        action={<Button size="sm" variant="outline" onClick={personas.add} className="rounded-sm"><Plus size={12} className="mr-1" />Agregar</Button>}
      >
        {personas.list.length === 0 && <div className="text-sm text-slate-500 py-2">Si lo dejas vacío, se usa el equipo por defecto del Perfil.</div>}
        <div className="space-y-2">
          {personas.list.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <span className="font-mono text-xs text-slate-500 col-span-1">#{i + 1}</span>
              <I value={p.nombre || ""} onChange={(e) => personas.set(i, { nombre: e.target.value })} placeholder="Nombre y Apellidos" className="col-span-7" />
              <I value={p.cargo || ""} onChange={(e) => personas.set(i, { cargo: e.target.value })} placeholder="Cargo (Ej. ENCARGADO DE BRIGADA)" className="col-span-3" />
              <Button size="sm" variant="ghost" onClick={() => personas.remove(i)} className="col-span-1 rounded-sm text-red-500"><Minus size={14} /></Button>
            </div>
          ))}
        </div>
      </Section>

      {/* Presentes en el acto (colindantes) */}
      <Section
        title="Presentes en el acto (colindantes)"
        progress={{ done: presCount, total: Math.max(presCount, 1) }}
        action={<Button size="sm" variant="outline" onClick={pres.add} className="rounded-sm"><Plus size={12} className="mr-1" />Agregar</Button>}
      >
        {pres.list.length === 0 && <div className="text-sm text-slate-500 py-2">Sin presentes registrados.</div>}
        <div className="space-y-2">
          {pres.list.map((p, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <I value={p.nombre || ""} onChange={(e) => pres.set(i, { nombre: e.target.value })} placeholder="Nombre y apellidos" className="col-span-4" />
              <I value={p.cedula || ""} onChange={(e) => pres.set(i, { cedula: e.target.value })} placeholder="Cédula / Pasaporte / RNC" className="col-span-3 font-mono" />
              <I value={p.calidad || ""} onChange={(e) => pres.set(i, { calidad: e.target.value })} placeholder="Calidad" className="col-span-2" />
              <I value={p.observaciones || ""} onChange={(e) => pres.set(i, { observaciones: e.target.value })} placeholder="Observaciones" className="col-span-2" />
              <Button size="sm" variant="ghost" onClick={() => pres.remove(i)} className="col-span-1 rounded-sm text-red-500"><Minus size={14} /></Button>
            </div>
          ))}
        </div>
      </Section>

      {/* Cierre */}
      <Section title="Cierre y firma" progress={pCierre}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Hojas en total"><I className="font-mono" value={sr.hojas_total || ""} onChange={(e) => upd("hojas_total", e.target.value)} placeholder="2" /></Field>
          <Field label="Lugar de firma del acta"><I value={sr.lugar_firma_acta || ""} onChange={(e) => upd("lugar_firma_acta", e.target.value)} placeholder="Santo Domingo, Distrito Nacional" /></Field>
          <Field label="Fecha de firma del acta"><I value={sr.fecha_firma_acta || ""} onChange={(e) => upd("fecha_firma_acta", e.target.value)} placeholder="DD de mes del AAAA" /></Field>
        </div>
      </Section>
    </div>
  );
}
